import type {
  Chain,
  Erc20Transfer,
  InternalTransactionWithIndex,
  LowerHex,
  TransactionIndex,
} from '@0xtorch/evm'
import type { AccountEvm } from '@pkg/database-portfolio'
import { retryWith401Handler } from '../utils'

/** Time in milliseconds for sync cooldown period (24 hours) */
const SYNC_COOLDOWN_MS = 24 * 60 * 60 * 1000 // 24 hours

/**
 * Parameters for fetching address transaction data.
 */
interface FetchAddressDataParams {
  account: AccountEvm
  chain: Chain
  getToken?: () => Promise<string | null>
  toBlock: number
  transactionCount: number
}

/**
 * Result object containing all fetched address transaction data.
 */
interface FetchAddressDataResult {
  indexes: readonly TransactionIndex[]
  internalTransactions: readonly InternalTransactionWithIndex[]
  erc20Transfers: readonly Erc20Transfer[]
}

/**
 * Function type for fetching address transaction data.
 */
type FetchAddressData = (
  params: FetchAddressDataParams,
) => Promise<FetchAddressDataResult>

/**
 * Determines whether to skip synchronization based on account state.
 * Skips sync if transaction count hasn't changed and within cooldown period.
 * @param account - The EVM account to check
 * @param transactionCount - The current transaction count
 * @returns True if sync should be skipped, false otherwise
 */
const shouldSkipSync = (
  account: AccountEvm,
  transactionCount: number,
): boolean => {
  const now = Date.now()
  const lastSyncedAt = account.lastSyncedAt ?? 0

  return (
    account.lastTransactionCount === transactionCount &&
    now - lastSyncedAt < SYNC_COOLDOWN_MS
  )
}

/**
 * Adds transaction indexes to a map, avoiding duplicates.
 * @param indexMap - The map to add indexes to
 * @param indexes - The array of transaction indexes to add
 */
const addIndexesToMap = (
  indexMap: Map<LowerHex, TransactionIndex>,
  indexes: readonly TransactionIndex[],
): void => {
  for (const index of indexes) {
    if (!indexMap.has(index.hash)) {
      indexMap.set(index.hash, index)
    }
  }
}

/**
 * Adds internal transaction indexes to a map, creating TransactionIndex objects.
 * @param indexMap - The map to add indexes to
 * @param internalTransactions - The array of internal transactions to process
 */
const addInternalTransactionIndexes = (
  indexMap: Map<LowerHex, TransactionIndex>,
  internalTransactions: readonly InternalTransactionWithIndex[],
): void => {
  for (const internalTransaction of internalTransactions) {
    if (!indexMap.has(internalTransaction.txHash)) {
      indexMap.set(internalTransaction.txHash, {
        hash: internalTransaction.txHash,
        blockNumber: internalTransaction.blockNumber,
        timestamp: internalTransaction.timestamp,
      })
    }
  }
}

const noRetryChainIds = new Set<number>([
  // Fantom
  250,
  // Ronin
  2020,
])

/**
 * Global queue for Etherscan API rate limiting.
 * Ensures all Etherscan API calls across all chains respect the 10 req/sec limit.
 */
let etherscanApiQueue = Promise.resolve()
const ETHERSCAN_RATE_LIMIT_MS = 350 // 350ms between calls = ~2.86 req/sec per chain, safe for multiple chains

/**
 * Queues an Etherscan API call to respect global rate limit.
 * @param fn - The function to execute
 * @returns Promise with the function result
 */
const queueEtherscanApiCall = async <T>(fn: () => Promise<T>): Promise<T> => {
  const previousCall = etherscanApiQueue

  let resolveCurrentCall: (value: T) => void = () => {}
  let rejectCurrentCall: (reason: unknown) => void = () => {}

  const currentCall = new Promise<T>((resolve, reject) => {
    resolveCurrentCall = resolve
    rejectCurrentCall = reject
  })

  etherscanApiQueue = currentCall.then(
    () =>
      new Promise((resolve) => setTimeout(resolve, ETHERSCAN_RATE_LIMIT_MS)),
    () =>
      new Promise((resolve) => setTimeout(resolve, ETHERSCAN_RATE_LIMIT_MS)),
  )

  await previousCall.catch(() => {
    // Ignore previous errors, just wait for the delay
  })

  try {
    const result = await fn()
    resolveCurrentCall(result)
    return result
  } catch (error) {
    rejectCurrentCall(error)
    throw error
  }
}

/**
 * Fetches all transaction-related data for an address sequentially for Etherscan API chains.
 * Retrieves normal transactions, token transfers, and internal transactions.
 * @param chain - The blockchain chain configuration
 * @param address - The address to fetch data for
 * @param fromBlock - The starting block number
 * @param toBlock - The ending block number
 * @param getToken - Optional function to get authentication token
 * @returns Object containing all fetched transaction data
 */
const fetchTransactionData = async (
  chain: Chain,
  address: LowerHex,
  fromBlock: number,
  toBlock: number,
  getToken?: () => Promise<string | null>,
) => {
  // Etherscan API chains (10 req/sec rate limit)
  const etherscanChainIds = new Set([
    1, // Ethereum Mainnet
    56, // BNB Smart Chain Mainnet
    137, // Polygon Mainnet
    10, // OP Mainnet
    42161, // Arbitrum One Mainnet
    43114, // Avalanche C-Chain
    100, // Gnosis
    2741, // Abstract Mainnet
    33139, // ApeChain Mainnet
    42170, // Arbitrum Nova Mainnet
    8453, // Base Mainnet
    80094, // Berachain Mainnet
    199, // BitTorrent Chain Mainnet
    81457, // Blast Mainnet
    42220, // Celo Mainnet
    25, // Cronos Mainnet
    252, // Fraxtal Mainnet
    999, // HyperEVM Mainnet
    59144, // Linea Mainnet
    5000, // Mantle Mainnet
    1284, // Moonbeam Mainnet
    1285, // Moonriver Mainnet
    747474, // Katana Mainnet
    1329, // Sei Mainnet
    534352, // Scroll Mainnet
    146, // Sonic Mainnet
    50104, // Sophon Mainnet
    1923, // Swellchain Mainnet
    167000, // Taiko Mainnet
    130, // Unichain Mainnet
    480, // World Mainnet
    50, // XDC Mainnet
    324, // zkSync Mainnet
    204, // opBNB Mainnet
  ])

  const createRequestParams = async () => ({
    address,
    fromBlock,
    toBlock,
    headers: await createExplorerRequestHeaders({ getToken }),
  })

  // For Etherscan API chains, use global queue to respect rate limit across all chains
  const isEtherscanChain = etherscanChainIds.has(chain.id)
  const shouldUseRetry = !noRetryChainIds.has(chain.id)

  const executeApiCall = async <T>(apiCall: () => Promise<T>): Promise<T> => {
    if (isEtherscanChain) {
      return queueEtherscanApiCall(async () => {
        if (shouldUseRetry) {
          return retryWith401Handler(apiCall)
        }
        return apiCall()
      })
    }
    if (shouldUseRetry) {
      return retryWith401Handler(apiCall)
    }
    return apiCall()
  }

  const normalTxIndexes = await executeApiCall(async () =>
    chain.explorer.getAddressTransactionIndexes(await createRequestParams()),
  )

  const tokenTransferData = await executeApiCall(async () =>
    chain.explorer.getAddressTokenTransferIndexes(await createRequestParams()),
  )

  const internalTransactions = await executeApiCall(async () =>
    chain.explorer.getAddressInternalTransactions(await createRequestParams()),
  )

  return {
    normalTxIndexes,
    transferTxIndexes: tokenTransferData.indexes,
    erc20Transfers: tokenTransferData.erc20Transfers,
    internalTransactions,
  }
}

/**
 * Fetches and processes all transaction data for an EVM address.
 * Implements caching logic to avoid unnecessary API calls and combines
 * normal transactions, token transfers, and internal transactions.
 * @param params - The parameters for fetching address data
 * @returns Promise containing transaction indexes, internal transactions, and ERC20 transfers
 */
export const fetchAddressData: FetchAddressData = async (params) => {
  const { account, chain, getToken, toBlock, transactionCount } = params

  if (shouldSkipSync(account, transactionCount)) {
    return {
      indexes: [],
      internalTransactions: [],
      erc20Transfers: [],
    }
  }

  const address = account.evmAddress
  const fromBlock = account.evmToBlock ?? 0

  const {
    normalTxIndexes,
    transferTxIndexes,
    erc20Transfers,
    internalTransactions,
  } = await fetchTransactionData(chain, address, fromBlock, toBlock, getToken)

  const indexMap = new Map<LowerHex, TransactionIndex>()
  addIndexesToMap(indexMap, normalTxIndexes)
  addIndexesToMap(indexMap, transferTxIndexes)
  addInternalTransactionIndexes(indexMap, internalTransactions)

  return {
    indexes: [...indexMap.values()],
    internalTransactions,
    erc20Transfers,
  }
}

/**
 * Parameters for creating explorer request headers.
 */
interface ExplorerRequestHeadersParams {
  getToken?: () => Promise<string | null>
}

/**
 * Creates request headers for blockchain explorer API calls.
 * Includes authentication token if available.
 * @param params - Parameters containing optional token getter function
 * @returns Headers object with Authorization bearer token, or undefined if no token
 */
const createExplorerRequestHeaders = async (
  params: ExplorerRequestHeadersParams,
): Promise<Record<string, string> | undefined> => {
  const { getToken } = params

  if (!getToken) {
    return undefined
  }

  const token = await getToken()

  return token ? { Authorization: `Bearer ${token}` } : undefined
}
